<template>
  <!--
    SymbologySwapModal — reveal-style picker for swapping the active
    astrology variant's symbology pool. Mirrors DeckSwapModal's pattern
    (carousel → click to reveal grid → confirm).

    Carousel face: the pool's first sign (Aries) so the user sees ONE
    representative image per pool. Click to fan out to all 12.
  -->
  <div class="modal-backdrop" @click.self="onBackdropClick">
    <div class="modal-card">
      <header class="modal-header">
        <div>
          <h2 class="modal-title">
            {{ revealedSlug ? (revealedPool?.name || 'Symbology Preview') : 'Swap your symbology' }}
          </h2>
          <p class="modal-sub">
            {{ revealedSlug
                 ? 'All 12 signs in this pool. Use this symbology to make it active.'
                 : 'Click a sign to flip the pool open and see all 12.' }}
          </p>
        </div>
        <button class="close-btn" @click="$emit('close')">×</button>
      </header>

      <div class="modal-body">
        <!-- ── Carousel ────────────────────────────────────────── -->
        <div v-if="!revealedSlug">
          <div v-if="loadingList" class="status">Loading symbology…</div>
          <div v-else-if="!pools.length" class="status">
            No symbology pools available.
          </div>
          <div v-else class="carousel">
            <button
              class="nav-btn left"
              :disabled="carouselIndex === 0"
              @click="carouselIndex = Math.max(0, carouselIndex - 1)"
            >‹</button>
            <div class="carousel-track">
              <button
                v-for="(p, i) in pools"
                :key="p.slug"
                type="button"
                class="sign-tile"
                :class="{ focused: i === carouselIndex, current: p.slug === currentPoolSlug }"
                @click="onSignClick(p, i)"
              >
                <img
                  v-if="firstSignOf(p)"
                  :src="firstSignOf(p)"
                  :alt="p.name + ' Aries'"
                />
                <div v-else class="sign-placeholder">{{ p.name }}</div>
                <div class="sign-meta">
                  <div class="sign-name">{{ p.name }}</div>
                  <div v-if="p.slug === currentPoolSlug" class="sign-current">Current</div>
                </div>
              </button>
            </div>
            <button
              class="nav-btn right"
              :disabled="carouselIndex >= pools.length - 1"
              @click="carouselIndex = Math.min(pools.length - 1, carouselIndex + 1)"
            >›</button>
          </div>
        </div>

        <!-- ── Reveal ──────────────────────────────────────────── -->
        <div v-else>
          <div v-if="loadingPreview" class="status">
            Revealing the symbology…
          </div>
          <div v-else-if="!revealedSigns.length" class="status">
            No signs generated yet for this pool.
          </div>
          <div v-else class="signs-grid">
            <div v-for="s in revealedSigns" :key="s.id" class="sign-grid-tile">
              <img :src="s.preview_url || s.image_url" :alt="s.sign" />
              <div class="sign-grid-name">{{ s.sign }}</div>
            </div>
          </div>
          <p v-if="confirmError" class="error">⚠ {{ confirmError }}</p>
        </div>
      </div>

      <footer class="modal-footer">
        <button v-if="revealedSlug" class="back-btn" @click="closeReveal" :disabled="confirming">
          ← Back to symbology
        </button>
        <span v-else></span>
        <button
          v-if="revealedSlug"
          class="use-btn"
          :disabled="!canConfirm || confirming"
          @click="confirmSwap"
        >
          {{ confirming ? 'Saving…' : (revealedSlug === currentPoolSlug ? 'Already your symbology' : 'Use this symbology →') }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { store } from '@/store'
import * as api from '@/api-service.js'

const props = defineProps({
  variant: { type: Object, required: true },
})
const emit = defineEmits(['close', 'saved'])

const pools = ref([])
const loadingList = ref(true)
const carouselIndex = ref(0)

const revealedSlug = ref('')
const revealedPool = ref(null)
const revealedSigns = ref([])
const loadingPreview = ref(false)

const confirming = ref(false)
const confirmError = ref('')

const currentPoolSlug = computed(() => props.variant?.symbology_pool_slug || '')
const canConfirm = computed(() => (
  revealedSlug.value && revealedSlug.value !== currentPoolSlug.value
))

function firstSignOf(p) {
  // Backend includes ``first_sign_image_url`` (Aries), with a fallback
  // to a random sample sign or the cover image.
  return p?.first_sign_image_url
    || (p?.sample_signs && p.sample_signs[0])
    || p?.cover_url
    || p?.preview_url
    || ''
}

async function loadPools() {
  loadingList.value = true
  try {
    const data = await api.browseSymbologyPools()
    pools.value = data?.items || []
    const idx = pools.value.findIndex(p => p.slug === currentPoolSlug.value)
    if (idx >= 0) carouselIndex.value = idx
  } finally {
    loadingList.value = false
  }
}
loadPools()

async function onSignClick(pool, idx) {
  carouselIndex.value = idx
  revealedSlug.value = pool.slug
  loadingPreview.value = true
  revealedPool.value = null
  revealedSigns.value = []
  try {
    const data = await api.previewSymbologyPool(pool.slug)
    if (data) {
      revealedPool.value = data.pool
      revealedSigns.value = data.signs || []
    }
  } finally {
    loadingPreview.value = false
  }
}

function closeReveal() {
  revealedSlug.value = ''
  revealedPool.value = null
  revealedSigns.value = []
  confirmError.value = ''
}

async function confirmSwap() {
  if (!canConfirm.value) return
  confirming.value = true
  confirmError.value = ''
  try {
    await api.chooseSymbologyPool(revealedSlug.value, {
      skill_key: 'astrology',
      variant_key: props.variant.variant_key || 'zodiac_daily',
    })
    const fresh = await api.listSkillVariants()
    store.readerSkillVariants = fresh
    emit('saved', revealedSlug.value)
  } catch (e) {
    confirmError.value = e?.response?.data?.error || String(e)
  } finally {
    confirming.value = false
  }
}

function onBackdropClick() {
  if (confirming.value) return
  emit('close')
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
}
.modal-card {
  background: #1a0c2e;
  border: 1px solid rgba(195,169,255,0.18);
  border-radius: 14px;
  width: 100%;
  max-width: 1080px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}
.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}
.modal-title {
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  font-size: 20px;
  margin: 0 0 4px;
}
.modal-sub {
  color: rgba(212, 196, 232, 0.7);
  font-size: 13px;
  margin: 0;
}
.close-btn {
  background: transparent;
  color: rgba(212,196,232,0.7);
  border: none;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  padding: 0 8px;
}
.close-btn:hover { color: #fff; }

.modal-body {
  padding: 18px 24px;
  overflow-y: auto;
  flex: 1;
  min-height: 380px;
}
.status {
  text-align: center;
  padding: 48px;
  color: rgba(212,196,232,0.7);
  font-style: italic;
}

.carousel {
  display: flex;
  align-items: center;
  gap: 12px;
}
.nav-btn {
  background: transparent;
  border: 1px solid rgba(195,169,255,0.32);
  color: rgba(212,196,232,0.85);
  width: 38px;
  height: 38px;
  border-radius: 50%;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}
.nav-btn:hover:not(:disabled) {
  border-color: #C9A94E;
  color: #C9A94E;
}
.nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}
.carousel-track {
  display: flex;
  gap: 14px;
  overflow-x: auto;
  scroll-behavior: smooth;
  flex: 1;
  padding: 8px 4px;
}
.sign-tile {
  flex: 0 0 200px;
  background: rgba(0,0,0,0.45);
  border: 2px solid rgba(195,169,255,0.18);
  border-radius: 12px;
  padding: 6px 6px 10px;
  cursor: pointer;
  font-family: inherit;
  color: inherit;
  text-align: center;
  transition: all 0.18s;
}
.sign-tile:hover {
  border-color: rgba(195,169,255,0.42);
  transform: translateY(-3px);
}
.sign-tile.focused {
  border-color: #C9A94E;
  box-shadow: 0 8px 26px rgba(0,0,0,0.45), 0 0 18px rgba(201,169,78,0.18);
}
.sign-tile.current {
  border-color: rgba(201,169,78,0.55);
}
.sign-tile img {
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: 8px;
  display: block;
}
.sign-placeholder {
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(212,196,232,0.55);
  font-size: 12px;
  font-style: italic;
  background: rgba(255,255,255,0.04);
  border-radius: 8px;
}
.sign-meta {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: center;
}
.sign-name {
  font-family: 'Cinzel', serif;
  color: rgba(232, 216, 255, 0.92);
  font-size: 13px;
  text-transform: capitalize;
}
.sign-current {
  font-size: 9px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #C9A94E;
}

.signs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  animation: reveal 0.32s ease-out;
}
@keyframes reveal {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
.sign-grid-tile {
  background: rgba(255,255,255,0.03);
  border-radius: 8px;
  padding: 8px;
  text-align: center;
}
.sign-grid-tile img {
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: 6px;
  display: block;
}
.sign-grid-name {
  font-size: 11px;
  color: rgba(212,196,232,0.85);
  margin-top: 6px;
  text-transform: capitalize;
}
.error {
  color: rgba(244, 67, 54, 0.9);
  font-size: 12px;
  text-align: center;
  margin: 12px 0 0;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid rgba(255,255,255,0.06);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}
.back-btn {
  background: transparent;
  color: rgba(212,196,232,0.75);
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
}
.back-btn:hover:not(:disabled) { color: #fff; }
.back-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.use-btn {
  background: #663399;
  color: #fff;
  border: none;
  padding: 12px 26px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.use-btn:hover:not(:disabled) { background: #7a3fb1; }
.use-btn:disabled { opacity: 0.5; cursor: not-allowed; }

@media (max-width: 720px) {
  .sign-tile { flex: 0 0 150px; }
  .modal-title { font-size: 17px; }
  .signs-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
}
</style>
